import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/state/store/auth';
import {
  createTimeSheetEntry,
  deleteTimeSheetEntry,
  getMbmProjectsLookup,
  getMyTimeSheets,
  updateTimeSheetEntry,
} from '../services/my-timesheet.service';
import { GetTimeSheetsParams, TimeSheetInsertInput, TimeSheetUpdateInput } from '../types/timesheet.types';

export const useCurrentUserId = () => {
  const user = useAuthStore((state) => state.user);
  return user?.itemId ?? '';
};

/**
 * The real backend (TimeSheet.Command handlers) resolves DisplayName/Email/
 * DepartmentId server-side from the requester's UserProfileDetail record.
 * Since this migration talks directly to the Blocks Data Gateway, this fills
 * in what's available from the authenticated user profile instead; the requester's
 * department scope fields are stamped by `createTimeSheetEntry` at insert
 * time (see `withUserScope`).
 */
export const useCurrentEmployeeSnapshot = () => {
  const user = useAuthStore((state) => state.user);
  return {
    UserId: user?.itemId ?? '',
    DisplayName: user ? `${user.firstName} ${user.lastName}`.trim() : undefined,
    Email: user?.email,
  };
};

const invalidateTimeSheetQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-my-timesheets'] });

export const useGetMyTimeSheets = (params: GetTimeSheetsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-my-timesheets', params],
    queryFn: () => getMyTimeSheets(params),
    staleTime: 60 * 1000,
  });
};

export const useGetMbmProjectsLookup = () => {
  return useGlobalQuery({
    queryKey: ['hr-mbm-projects-lookup'],
    queryFn: () => getMbmProjectsLookup(),
    staleTime: 30 * 60 * 1000,
  });
};

export const useCreateTimeSheetEntry = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: TimeSheetInsertInput) => createTimeSheetEntry(input),
    onSuccess: () => {
      invalidateTimeSheetQueries(queryClient);
      toast({ title: t('MY_TIMESHEET_ENTRY_SAVED') });
    },
  });
};

export const useUpdateTimeSheetEntry = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: TimeSheetUpdateInput }) =>
      updateTimeSheetEntry(params.itemId, params.input),
    onSuccess: () => {
      invalidateTimeSheetQueries(queryClient);
      toast({ title: t('MY_TIMESHEET_ENTRY_UPDATED') });
    },
  });
};

export const useDeleteTimeSheetEntry = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteTimeSheetEntry(itemId),
    onSuccess: () => {
      invalidateTimeSheetQueries(queryClient);
      toast({ title: t('MY_TIMESHEET_ENTRY_DELETED') });
    },
  });
};
